import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { activeTodos, allTags, useStore, visibleNotes } from '../store'
import { DAYS_IN_TRASH, type Note, type SortKey } from '../types'
import { dateGroupLabel, daysLeftInTrash, firstImage, listDate } from '../lib/format'
import { ContextMenu, type MenuItem, type MenuState, menuPos } from './ContextMenu'
import { Toast } from './Toast'
import { useSwipe } from '../lib/useSwipe'
import { type Departing, useDeparting } from '../lib/useDeparting'
import { dragPayload, setDragIds } from '../lib/dnd'
import { shareNote } from '../lib/share'
import { attachDragImage } from '../lib/dragImage'
import {
  ChecklistIcon, ChevronLeft, ComposeIcon, GridIcon, ListViewIcon, PinIcon, SearchIcon,
  SidebarIcon, TrashIcon,
} from './icons'

const SORTS: Array<{ key: SortKey; label: string }> = [
  { key: 'edited', label: 'Date Edited' },
  { key: 'created', label: 'Date Created' },
  { key: 'title', label: 'Title' },
]

const isDesktopShell = typeof window !== 'undefined' && 'electronShell' in window

interface RowProps {
  note: Note; selected: boolean; lead?: boolean; departing?: boolean
  onClick: (id: string, e: React.MouseEvent) => void
  onMenu: (id: string, e: React.MouseEvent) => void
  onSwipeDelete?: (id: string) => void
  onSwipePin?: (id: string) => void
  onDragStart?: (id: string, e: React.DragEvent) => void
}

/**
 * Memoised, and given callbacks whose identity never changes: a snapshot from
 * Firestore replaces the whole notes array, and without this every row in the
 * list re-rendered — each one re-running useSwipe — in the same frame the
 * delete animation was trying to start.
 */
const Row = memo(function Row({
  note, selected, lead, departing, onClick, onMenu, onSwipeDelete, onSwipePin, onDragStart,
}: RowProps) {
  const inTrash = note.trashedAt != null
  const swipeDelete = useCallback(() => onSwipeDelete?.(note.id), [onSwipeDelete, note.id])
  const swipePin = useCallback(() => onSwipePin?.(note.id), [onSwipePin, note.id])
  const swipe = useSwipe(
    departing || !onSwipeDelete ? undefined : swipeDelete,
    departing || !onSwipePin ? undefined : swipePin,
  )
  return (
    <div className={`note-row-swipe ${departing ? 'departing' : ''}`} aria-hidden={departing}>
      {/* Revealed beneath the row as it slides. */}
      <span className="swipe-action left" aria-hidden>{note.pinned ? 'Unpin' : 'Pin'}</span>
      <span className="swipe-action right" aria-hidden>Delete</span>
      <button data-note-id={note.id} role="option" aria-selected={selected}
            draggable={!departing}
            onDragStart={(e) => onDragStart?.(note.id, e)}
            style={{ transform: swipe.offset ? `translateX(${swipe.offset}px)` : undefined }}
            {...swipe.bind}
            className={`note-row ${selected ? 'selected' : ''} ${lead ? 'lead' : ''} ${swipe.offset ? 'swiping' : ''}`}
            tabIndex={departing ? -1 : undefined}
            onClick={(e) => { if (!swipe.offset && !departing) onClick(note.id, e) }}
            onContextMenu={departing ? undefined : (e) => onMenu(note.id, e)}>
      <div className="row-title">
        <span className="row-title-text">{note.title || 'New Note'}</span>
        {note.pinned && !inTrash && <span className="pin"><PinIcon size={11} /></span>}
      </div>
      <div className="row-sub">
        <span className="row-date">
          {inTrash ? `${daysLeftInTrash(note.trashedAt!, DAYS_IN_TRASH)} days` : listDate(note.updatedAt)}
        </span>
        {!inTrash && <span className="row-snippet">{note.snippet || 'No additional text'}</span>}
      </div>
      </button>
    </div>
  )
})

const Card = memo(function Card({ note, selected, departing, onClick, onMenu, onDragStart }: {
  note: Note; selected: boolean; departing?: boolean
  onClick: (id: string, e: React.MouseEvent) => void
  onMenu: (id: string, e: React.MouseEvent) => void
  onDragStart?: (id: string, e: React.DragEvent) => void
}) {
  const thumb = useMemo(() => firstImage(note.content), [note.content])
  return (
    <button data-note-id={note.id} role="option" aria-selected={selected}
            aria-hidden={departing} tabIndex={departing ? -1 : undefined}
            draggable={!departing}
            onDragStart={(e) => onDragStart?.(note.id, e)}
            className={`note-card ${selected ? 'selected' : ''} ${departing ? 'departing' : ''}`}
            onClick={departing ? undefined : (e) => onClick(note.id, e)}
            onContextMenu={departing ? undefined : (e) => onMenu(note.id, e)}>
      <div className="card-preview">
        {thumb
          ? <img src={thumb} alt="" loading="lazy" />
          : <div className="card-text">{note.snippet || note.title}</div>}
      </div>
      <div className="card-title">{note.title || 'New Note'}</div>
      <div className="card-date">{listDate(note.updatedAt)}</div>
    </button>
  )
})

export function NoteList({ onBack, onOpen, showSidebarToggle, onShowSidebar }: {
  onBack: () => void
  onOpen: () => void
  showSidebarToggle?: boolean
  onShowSidebar?: () => void
}) {
  const store = useStore()
  const scope = store.scope
  const notes = useMemo(() => visibleNotes(store), [store])
  // Deleted notes stay on screen briefly so the list can close the gap around
  // them. Scope, search and sort each produce a different list, not a deletion.
  const shown = useDeparting(notes, `${JSON.stringify(scope)}|${store.query}|${store.sortKey}`)
  const tags = useMemo(() => allTags(store), [store])
  const inTrash = scope.kind === 'trash'
  const [menu, setMenu] = useState<MenuState | null>(null)
  const [toast, setToast] = useState<string | null>(null)

  const title = inTrash
    ? 'Recently Deleted'
    : scope.kind === 'folder'
      ? store.folders.find((f) => f.id === scope.id)?.name ?? 'Notes'
      : scope.kind === 'tag'
        ? `#${scope.tag}`
        : 'All Notes'

  const listRef = useRef<HTMLDivElement>(null)
  /** The notes an action applies to: the multi-selection, else the open note. */
  const selection = useMemo(
    () => (store.selectedIds.length ? store.selectedIds : store.selectedId ? [store.selectedId] : []),
    [store.selectedIds, store.selectedId],
  )
  const narrow = () => window.matchMedia('(max-width: 820px)').matches

  const open = (id: string) => {
    if (narrow()) {
      // Start the slide before loading the note, not after. Selecting a note
      // puts its document into the editor, which costs ~40ms on a phone — a
      // main-thread stall long enough to swallow the first frames of the
      // transition, so the pane appeared to jump rather than slide. The
      // transform is composited, so once the animation has started that work
      // no longer blocks it.
      onOpen()
      requestAnimationFrame(() => {
        store.select(id)
        // On a phone the editor is a whole screen, so it should take the caret.
        store.requestEditorFocus(id)
      })
      return
    }
    store.select(id)
    // On desktop focus stays in the list, which is what makes the arrow keys work.
    listRef.current?.focus()
    onOpen()
  }

  /** The run of notes between two rows, in the order the list shows them. */
  const rangeBetween = (fromId: string, toId: string) => {
    const a = ordered.findIndex((n) => n.id === fromId)
    const b = ordered.findIndex((n) => n.id === toId)
    if (a < 0 || b < 0) return [toId]
    return ordered.slice(Math.min(a, b), Math.max(a, b) + 1).map((n) => n.id)
  }

  /**
   * Shift extends from the anchor, Cmd/Ctrl adds or removes one note, and a
   * plain click replaces the selection — the same three gestures as Finder.
   */
  const clickRow = (id: string, e: React.MouseEvent) => {
    if (e.shiftKey) {
      e.preventDefault()   // a shift-click would otherwise select the page text
      const anchor = store.anchorId ?? store.selectedId
      store.selectRange(anchor ? rangeBetween(anchor, id) : [id], id)
      listRef.current?.focus()
      return
    }
    if (e.metaKey || e.ctrlKey) {
      store.toggleSelected(id)
      listRef.current?.focus()
      return
    }
    open(id)
  }

  /** Right-clicking inside a multi-selection acts on all of it, like Finder. */
  const menuForMany = (ids: string[]): MenuItem[] => {
    const notes = store.notes.filter((n) => ids.includes(n.id))
    const allPinned = notes.every((n) => n.pinned)
    if (inTrash) {
      return [
        { label: `Recover ${ids.length} Notes`, onClick: () => ids.forEach((id) => store.restoreNote(id)) },
        { type: 'separator' },
        {
          label: `Delete ${ids.length} Notes Immediately`,
          danger: true,
          onClick: () => {
            void store.ask({
              title: `Delete these ${ids.length} notes permanently?`,
              confirmLabel: 'Delete', danger: true,
            }).then((ok) => { if (ok) store.deleteForeverMany(ids) })
          },
        },
      ]
    }
    return [
      {
        label: allPinned ? `Unpin ${ids.length} Notes` : `Pin ${ids.length} Notes`,
        onClick: () => store.setPinnedMany(ids, !allPinned),
      },
      { type: 'separator' },
      {
        label: `Move ${ids.length} Notes to`,
        submenu: [
          { label: 'Notes', onClick: () => store.moveMany(ids, null) },
          ...(store.folders.length ? [{ type: 'separator' } as MenuItem] : []),
          ...store.folders.map((f) => ({ label: f.name, onClick: () => store.moveMany(ids, f.id) })),
        ],
      },
      { type: 'separator' },
      { label: `Delete ${ids.length} Notes`, danger: true, onClick: () => store.trashMany(ids) },
    ]
  }

  const menuFor = (note: Note): MenuItem[] => {
    if (selection.length > 1 && selection.includes(note.id)) return menuForMany(selection)
    if (note.trashedAt != null) {
      return [
        { label: 'Recover', onClick: () => store.restoreNote(note.id) },
        { type: 'separator' },
        { label: 'Delete Immediately', danger: true, onClick: () => store.deleteForever(note.id) },
      ]
    }
    return [
      ...(isDesktopShell
        ? [{
            label: 'Open Note in New Window',
            onClick: () => window.open(`${location.pathname}?note=${note.id}`, '_blank'),
          } as MenuItem, { type: 'separator' } as MenuItem]
        : []),
      { label: note.pinned ? 'Unpin Note' : 'Pin Note', onClick: () => store.togglePin(note.id) },
      { type: 'separator' },
      { label: 'New Note', onClick: () => { store.newNote(); onOpen() } },
      { label: 'Duplicate Note', onClick: () => { store.duplicateNote(note.id); onOpen() } },
      { type: 'separator' },
      {
        label: 'Tags',
        submenu: tags.length
          ? tags.map((t) => ({
              label: `#${t.tag}`,
              checked: (note.tags ?? []).includes(t.tag),
              onClick: () => store.addTagToNote(note.id, t.tag),
            }))
          : [{ label: 'No Tags Yet', disabled: true }],
      },
      {
        label: 'Move to',
        submenu: [
          { label: 'Notes', checked: note.folderId === null, onClick: () => store.moveNote(note.id, null) },
          ...(store.folders.length ? [{ type: 'separator' } as MenuItem] : []),
          ...store.folders.map((f) => ({
            label: f.name,
            checked: note.folderId === f.id,
            onClick: () => store.moveNote(note.id, f.id),
          })),
        ],
      },
      { type: 'separator' },
      {
        label: 'Share Note',
        onClick: () => void shareNote(note).then((outcome) => {
          // A copy is silent otherwise, and the user is left wondering whether
          // anything happened at all.
          if (outcome === 'copied') setToast('Note copied to the clipboard')
          else if (outcome === 'failed') setToast('Could not share that note')
        }),
      },
      { type: 'separator' },
      { label: 'Version History', onClick: () => store.openHistory(note.id) },
      { label: 'Delete', danger: true, onClick: () => store.trashNote(note.id) },
    ]
  }

  const onMenu = (note: Note, e: React.MouseEvent) => {
    // Right-clicking a row outside the selection moves the selection to it,
    // so the menu always describes what is actually highlighted.
    if (!selection.includes(note.id)) store.select(note.id)
    setMenu({ ...menuPos(e), items: menuFor(note) })
  }

  /**
   * The row callbacks, behind identities that never change. They read the
   * current closure through a ref, so memoised rows survive a re-render
   * without going stale.
   */
  const live = useRef({ clickRow, onMenu, store, inTrash, selection })
  live.current = { clickRow, onMenu, store, inTrash, selection }
  const rowHandlers = useMemo(() => ({
    click: (id: string, e: React.MouseEvent) => live.current.clickRow(id, e),
    menu: (id: string, e: React.MouseEvent) => {
      const n = live.current.store.notes.find((x) => x.id === id)
      if (n) live.current.onMenu(n, e)
    },
    swipeDelete: (id: string) => {
      const s = live.current
      if (s.inTrash) s.store.deleteForever(id)
      else s.store.trashNote(id)
    },
    swipePin: (id: string) => {
      const s = live.current
      if (!s.inTrash) s.store.togglePin(id)
    },
    dragStart: (id: string, e: React.DragEvent) => {
      if (!e.dataTransfer) return
      const ids = dragPayload(id, live.current.selection)
      setDragIds(e.dataTransfer, ids)
      const note = live.current.store.notes.find((n) => n.id === id)
      attachDragImage(e.dataTransfer, note?.title ?? '', ids.length)
    },
  }), [])

  // Pinned first, then Apple's date sections (skipped when sorting by title).
  const sections = useMemo(() => {
    const pinned = shown.filter((n) => n.pinned && !inTrash)
    const rest = shown.filter((n) => !n.pinned || inTrash)
    const out: Array<{ label: string; notes: Array<Note & Departing> }> = []
    if (pinned.length) out.push({ label: 'Pinned', notes: pinned })
    if (store.sortKey === 'title' || inTrash) {
      if (rest.length) out.push({ label: pinned.length ? 'Notes' : '', notes: rest })
      return out
    }
    let current: { label: string; notes: Array<Note & Departing> } | null = null
    for (const n of rest) {
      const label = dateGroupLabel(store.sortKey === 'created' ? n.createdAt : n.updatedAt)
      if (!current || current.label !== label) { current = { label, notes: [] }; out.push(current) }
      current.notes.push(n)
    }
    return out
  }, [shown, inTrash, store.sortKey])

  /** Flattened display order, so Up/Down crosses section boundaries. A row
   *  animating out is no longer a note; the selection must step over it. */
  const ordered = useMemo(
    () => sections.flatMap((sec) => sec.notes).filter((n) => !n.departing),
    [sections],
  )

  const step = (delta: number, extend = false) => {
    if (!ordered.length) return
    const at = ordered.findIndex((n) => n.id === store.selectedId)
    const next = at < 0 ? (delta > 0 ? 0 : ordered.length - 1)
      : Math.min(ordered.length - 1, Math.max(0, at + delta))
    const id = ordered[next].id
    if (!extend) { store.select(id); return }
    const anchor = store.anchorId ?? store.selectedId ?? id
    store.selectRange(rangeBetween(anchor, id), id)
  }

  // Keep the selected row visible as the selection walks past the viewport.
  useEffect(() => {
    if (!store.selectedId) return
    listRef.current
      ?.querySelector<HTMLElement>(`[data-note-id="${store.selectedId}"]`)
      ?.scrollIntoView({ block: 'nearest' })
  }, [store.selectedId])

  const onListKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') { e.preventDefault(); step(1, e.shiftKey) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); step(-1, e.shiftKey) }
    else if (e.key === 'a' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      if (ordered.length) store.selectRange(ordered.map((n) => n.id), ordered[0].id)
    }
    else if (e.key === 'Enter' || e.key === 'Tab') {
      if (!store.selectedId) return
      e.preventDefault()
      store.requestEditorFocus(store.selectedId)   // hand off to the editor
    }
    else if (e.key === 'Delete' || e.key === 'Backspace') {
      if (!store.selectedId) return
      e.preventDefault()
      const ids = selection
      // Keep the list moving: select the neighbour the deletion leaves behind.
      const remaining = ordered.filter((n) => !ids.includes(n.id))
      const at = ordered.findIndex((n) => n.id === store.selectedId)
      const neighbour = remaining.find((n) => ordered.indexOf(n) > at) ?? remaining.at(-1) ?? null
      if (inTrash) {
        void store.ask({
          title: ids.length > 1
            ? `Delete these ${ids.length} notes permanently?`
            : 'Delete this note permanently?',
          confirmLabel: 'Delete',
          danger: true,
        }).then((ok) => {
          if (!ok) return
          store.deleteForeverMany(ids)
          if (neighbour) store.select(neighbour.id)
        })
      } else {
        store.trashMany(ids)
        if (neighbour) store.select(neighbour.id)
      }
    }
    else if (e.key === 'Escape' && selection.length > 1) {
      e.preventDefault()
      store.select(store.selectedId)
    }
  }

  return (
    <section className="pane-list" onMouseDown={() => document.body.setAttribute('data-focus-pane', 'list')}>
      <div className="toolbar">
        <button className="back-btn" onClick={onBack}><ChevronLeft size={16} />Folders</button>
        {/* Lives inside the toolbar so it inherits no-drag — a floating button
            over the drag region is unclickable in Electron. */}
        {showSidebarToggle && (
          <button className="tb-btn" title="Show Sidebar" onClick={onShowSidebar}><SidebarIcon /></button>
        )}
        <div className="view-toggle">
          <button className={`tb-btn ${store.viewMode === 'list' ? 'on' : ''}`} title="List View"
                  onClick={() => store.setViewMode('list')}><ListViewIcon /></button>
          <button className={`tb-btn ${store.viewMode === 'gallery' ? 'on' : ''}`} title="Gallery View"
                  onClick={() => store.setViewMode('gallery')}><GridIcon /></button>
        </div>
        <div className="tb-spacer" />

        {/* Apple keeps compose in the editor toolbar; on a phone the editor is a
            separate screen, so it has to live here too. */}
        <button className="tb-btn only-narrow" title="New Note" onClick={() => { store.newNote(); onOpen() }}>
          <ComposeIcon />
        </button>

        <button
          className="tb-btn danger"
          title={inTrash ? 'Empty Recently Deleted' : 'Delete Note'}
          disabled={inTrash ? !notes.length : !store.selectedId}
          onClick={() => {
            if (inTrash) {
              void store.ask({
                title: 'Delete all notes in Recently Deleted?',
                message: 'This cannot be undone.', confirmLabel: 'Delete All', danger: true,
              }).then((ok) => { if (ok) store.emptyTrash() })
            } else if (store.selectedId) {
              store.trashNote(store.selectedId)
            }
          }}
        >
          <TrashIcon />
        </button>
      </div>

      <div className="search-wrap">
        <div className="search">
          <SearchIcon size={13} />
          <input placeholder={`Search ${title}`} value={store.query}
                 onChange={(e) => store.setQuery(e.target.value)} />
        </div>
      </div>

      <div
        ref={listRef}
        tabIndex={0}
        role="listbox"
        aria-label="Notes"
        onKeyDown={onListKeyDown}
        className={`list-scroll ${store.viewMode === 'gallery' ? 'gallery' : ''}`}
        onContextMenu={(e) => {
          if ((e.target as HTMLElement).closest('.note-row, .note-card')) return
          setMenu({
            ...menuPos(e),
            items: [
              { label: 'New Note', onClick: () => { store.newNote(); onOpen() } },
              { type: 'separator' },
              ...SORTS.map((so) => ({
                label: `Sort by ${so.label}`,
                checked: store.sortKey === so.key,
                onClick: () => store.setSortKey(so.key),
              })),
            ],
          })
        }}
      >
        {/* Shortcut into the To Do page, only from All Notes. */}
        {scope.kind === 'all' && !store.query && (
          <button className="todo-link" onClick={() => store.setScope({ kind: 'todo' })}>
            <span className="todo-link-icon"><ChecklistIcon /></span>
            <span className="todo-link-name">To Do</span>
            <span className="todo-link-count">{activeTodos(store).length || ''}</span>
            <span className="todo-link-chevron"><ChevronLeft size={13} /></span>
          </button>
        )}

        {!notes.length && (
          <div className="empty-state small" style={{ paddingTop: 40 }}>
            {store.query ? 'No Results' : inTrash ? 'No Recently Deleted Notes' : 'No Notes'}
          </div>
        )}

        {sections.map((sec) => (
          <div
            key={sec.label || 'none'}
            className={`list-section-group ${sec.notes.every((n) => n.departing) ? 'departing' : ''}`}
          >
            {sec.label && <div className="list-section">{sec.label}</div>}
            {store.viewMode === 'gallery' ? (
              <div className="card-grid">
                {sec.notes.map((n) => (
                  <Card key={n.id} note={n} selected={selection.includes(n.id)}
                        departing={n.departing}
                        onClick={rowHandlers.click} onMenu={rowHandlers.menu}
                        onDragStart={rowHandlers.dragStart} />
                ))}
              </div>
            ) : (
              sec.notes.map((n) => (
                <Row key={n.id} note={n} selected={selection.includes(n.id)}
                     lead={n.id === store.selectedId}
                     departing={n.departing}
                     onClick={rowHandlers.click} onMenu={rowHandlers.menu}
                     onSwipeDelete={rowHandlers.swipeDelete}
                     onSwipePin={inTrash ? undefined : rowHandlers.swipePin}
                     onDragStart={rowHandlers.dragStart} />
              ))
            )}
          </div>
        ))}
      </div>

      <ContextMenu state={menu} onClose={() => setMenu(null)} />
      {toast && <Toast message={toast} onDone={() => setToast(null)} />}
    </section>
  )
}
